import { checkedTypes, modalTypes } from "constants/types";
import { isExcel, renameKeys } from "utils";
import { showErrorModal } from "slices/app/actions/error";
import {
  addToJobQueue,
  clearJobQueue,
  setActionRequired,
} from "slices/app/appSlice";
import localhost from "apis/localhost";
import { folderBy } from "layout/dictionarySearch/constants";
import { refreshView } from "slices/dictionarySearch/actions/config";
import parse from "html-react-parser";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  setCategories,
  setDictionaries,
} from "slices/dictionarySearch/dictionarySearchSlice";

const finalKeys = {
  idx: "index",
  fq: "occurrences",
  cats: "categories",
  cNme: "name",
  cC: "colour",
  cDesc: "description",
  iNme: "name",
  iDesc: "description",
  docs: "documents",
  dict: "dictionary",
  nme: "name",
  wc: "wordCount",
  pc: "paragraphCount",
  iWc: "wildcard",
  iCs: "caseSensitive",
  iC: "colour",
  tt: "dictionaryTermType",
};

const setExpandedAndChecked = (data) => ({
  ...data,
  documents: data.documents.map((d) => ({
    ...d,
    checkState:
      d.wordCount > 0 || (isExcel(d.name) && d.paragraphCount > 0)
        ? checkedTypes.CHECKED
        : checkedTypes.EMPTY,
  })),
  dictionary: {
    ...data.dictionary,
    categories: data.dictionary.categories.map((category) => ({
      ...category,
      expanded: false,
    })),
  },
});

const collapseDocuments = (data) =>
  data.map((d) => ({ ...d, expanded: false }));

export const fetchCategories = (by) => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const params = {
    folderId: getState().dictionarySearch.config.folderId,
  };

  await localhost
    .get(`/docs/dictionarySearch/folder/${by}/categories`, {
      params,
    })
    .then(async (response) => {
      let { data } = response;
      data = renameKeys(data, finalKeys);
      data =
        by === folderBy.folder
          ? setExpandedAndChecked(data)
          : collapseDocuments(data);
      dispatch(setCategories(data));
    })
    .catch((error) => {
      if (error.response?.status === 409) {
        dispatch(
          setActionRequired({
            message: parse(error.response.data.error),
            buttonContent: "Refresh",
            buttonAction: () => dispatch(refreshView(true)),
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const fetchDocument = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const params = {
    folderId: getState().dictionarySearch.config.folderId,
    docId: getState().dictionarySearch.config.documentId,
  };

  await localhost
    .get(`/docs/dictionarySearch/document/categories`, {
      params,
    })
    .then(async (response) => {
      let { data } = response;
      data = renameKeys(data, finalKeys);
      data = collapseDocuments(data);
      dispatch(setCategories(data));
    })
    .catch((error) => {
      if (error.response?.status === 409) {
        dispatch(
          setActionRequired({
            message: parse(error.response.data.error),
            buttonContent: "Refresh",
            buttonAction: () => dispatch(refreshView(false)),
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const getDictionaries = (docId) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));
  await localhost
    .get(`/docs/dictionarySearch/change/list`)
    .then(async (response) => {
      const { data } = response;
      dispatch(setDictionaries(data));
      dispatch(showModal(modalTypes.CHANGE_DICTIONARY, { docId }));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

const replaceDocument = (documents, targetId, newDocument) => {
  const oldDocIndex = documents.findIndex((d) => d.id === targetId);

  return documents.map((d, index) => {
    if (index === oldDocIndex) {
      return newDocument;
    }
    return d;
  });
};

export const setNewDictionary =
  (dictId, docId) => async (dispatch, getState) => {
    dispatch(hideModal(modalTypes.CHANGE_DICTIONARY));
    dispatch(addToJobQueue("genericJob"));
    const url =
      getState().dictionarySearch.config.folderBy === folderBy.folder
        ? "/folder"
        : "/document";
    const params = {
      folderId: getState().dictionarySearch.config.folderId,
      documentId: docId,
      dictionaryId: dictId,
    };
    await localhost
      .post(`/docs/dictionarySearch/change${url}`, null, {
        params,
      })
      .then(async (response) => {
        let { data } = response;
        data = renameKeys(data, finalKeys);
        data =
          getState().dictionarySearch.config.folderBy === folderBy.folder
            ? setExpandedAndChecked(data)
            : collapseDocuments(
                replaceDocument(
                  getState().dictionarySearch.documents,
                  docId,
                  data[0]
                )
              );
        dispatch(setCategories(data));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const setDictionaryToAllDocs =
  (dictId) => async (dispatch, getState) => {
    dispatch(hideModal(modalTypes.CHANGE_DICTIONARY));
    dispatch(addToJobQueue("genericJob"));
    const params = {
      folderId: getState().dictionarySearch.config.folderId,
      dictionaryId: dictId,
    };
    await localhost
      .post(`/docs/dictionarySearch/change/documents`, null, {
        params,
      })
      .then(async (response) => {
        let { data } = response;
        data = renameKeys(data, finalKeys);
        data =
          getState().dictionarySearch.config.folderBy === folderBy.folder
            ? setExpandedAndChecked(data)
            : collapseDocuments(replaceDocument(data));
        dispatch(setCategories(data));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };
